import type { StateCreator } from "zustand";
import type { NewsFeedFiltersSlice } from "../newsFeed-filter.slice";

export type NarrativeClusterManagementActions = {
  setNarrativeClusterDeleteModalOpen: (isOpen: boolean) => void;
  setRenameClusterModalOpen: (isOpen: boolean) => void;
  setBulkMergeAndDeleteModalOpen: (
    isOpen: boolean,
    mode?: "merge" | "delete",
  ) => void;
  setSelectedCluster: (
    cluster: {
      id: string;
      name: string;
    } | null,
  ) => void;
  setClusterName: (name: string | null) => void;
  setSelectedClusterIds: (clusterIds: Set<string>) => void;
  toggleClusterSelection: (clusterId: string) => void;
  setLlmPerceptionModalOpen: (isOpen: boolean) => void;
  setLlmPerceptionClusterId: (clusterId: string | null) => void;
  resetRenameModalState: () => void;
  resetDeleteModalState: () => void;
  resetLlmPerceptionModalState: () => void;
  resetModalState: () => void;
};

export const createNarrativeClusterManagementActions: StateCreator<
  NewsFeedFiltersSlice,
  [["zustand/devtools", never]],
  [],
  NarrativeClusterManagementActions
> = (set, get) => ({
  setNarrativeClusterDeleteModalOpen: (isOpen: boolean) =>
    set(
      {
        isNarrativeClusterDeleteModalOpen: isOpen,
      },
      false,
      "[User] Narrative Cluster: Set delete modal open",
    ),

  setRenameClusterModalOpen: (isOpen: boolean) =>
    set(
      {
        isRenameClusterModalOpen: isOpen,
      },
      false,
      "[User] Narrative Cluster: Set rename modal open",
    ),

  setBulkMergeAndDeleteModalOpen: (
    isOpen: boolean,
    mode?: "merge" | "delete",
  ) =>
    set(
      {
        isBulkMergeAndDeleteModalOpen: isOpen,
        bulkActionMode: isOpen && mode ? mode : null,
      },
      false,
      mode
        ? `[User] Narrative Cluster: Set bulk ${mode} modal open`
        : "[User] Narrative Cluster: Set bulk merge and delete modal open",
    ),

  setSelectedCluster: (cluster) =>
    set(
      {
        selectedClusterId: cluster?.id ?? null,
        clusterName: cluster?.name ?? null,
      },
      false,
      "[User] Narrative Cluster: Set selected cluster",
    ),

  setClusterName: (name) =>
    set(
      {
        clusterName: name,
      },
      false,
      "[User] Narrative Cluster: Set cluster name",
    ),

  setSelectedClusterIds: (clusterIds) =>
    set(
      {
        selectedClusterIds: clusterIds,
      },
      false,
      "[User] Narrative Cluster: Set selected cluster IDs",
    ),

  toggleClusterSelection: (clusterId: string) => {
    const currentIds = get().selectedClusterIds;
    const newIds = new Set(currentIds);

    if (newIds.has(clusterId)) {
      newIds.delete(clusterId);
    } else {
      newIds.add(clusterId);
    }

    set(
      {
        selectedClusterIds: newIds,
      },
      false,
      "[User] Narrative Cluster: Toggle cluster selection",
    );
  },

  resetRenameModalState: () =>
    set(
      {
        isRenameClusterModalOpen: false,
        clusterName: null,
        isRenamingCluster: false,
      },
      false,
      "[User] Narrative Cluster: Reset rename modal state",
    ),

  resetDeleteModalState: () =>
    set(
      {
        isNarrativeClusterDeleteModalOpen: false,
        selectedClusterId: null,
        clusterName: null,
        isDeletingNarrativeCluster: false,
      },
      false,
      "[User] Narrative Cluster: Reset delete modal state",
    ),

  setLlmPerceptionModalOpen: (isOpen: boolean) =>
    set(
      {
        isLlmPerceptionModalOpen: isOpen,
      },
      false,
      "[User] Narrative Cluster: Set LLM perception modal open",
    ),

  setLlmPerceptionClusterId: (clusterId: string | null) =>
    set(
      {
        llmPerceptionClusterId: clusterId,
      },
      false,
      "[User] Narrative Cluster: Set LLM perception cluster ID",
    ),

  resetLlmPerceptionModalState: () =>
    set(
      {
        isLlmPerceptionModalOpen: false,
        llmPerceptionClusterId: null,
      },
      false,
      "[User] Narrative Cluster: Reset LLM perception modal state",
    ),

  resetModalState: () =>
    set(
      {
        isNarrativeClusterDeleteModalOpen: false,
        isRenameClusterModalOpen: false,
        isBulkMergeAndDeleteModalOpen: false,
        bulkActionMode: null,
        selectedClusterId: null,
        selectedClusterIds: new Set(),
        clusterName: null,
        isDeletingNarrativeCluster: false,
        isRenamingCluster: false,
        isLlmPerceptionModalOpen: false,
        llmPerceptionClusterId: null,
      },
      false,
      "[User] Narrative Cluster: Reset modal state",
    ),
});
